import { useEffect, useState } from "react";

import { PositionType } from "@type/HRM/position";
import { PaginationRequestType, PaginationType } from "@type/HRM/pagination";
import {
  downloadExtraPositionReport,
  getExtraPositionInformations,
} from "@api/extraPositionInformations";
import { useKeycloak } from "@react-keycloak/web";

interface UseExtraPositionsInformations {
  extraPositionsInfo: PositionType[];
  pagination: PaginationType;
  handleQuery: (key: string, value: string | number | null) => void;
  downloadReport: (fileName: string) => Promise<void>;
  isLoading: boolean;
}

export const useExtraPositionsInformations =
  (): UseExtraPositionsInformations => {
    const [extraPositionsInfo, setExtraPositionsInfo] = useState<
      PositionType[]
    >([]);
    const [pagination, setPagination] = useState<PaginationType>({
      page: 1,
      size: 10,
      total: 0,
    });
    const [query, setQuery] = useState<PaginationRequestType>({
      page: 1,
      size: 10,
    });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const { keycloak } = useKeycloak();
    const token = keycloak.token as string;

    const handleQuery = (key: string, value: string | number | null) =>
      setQuery((prev) => ({
        ...prev,
        [key]: value,
      }));

    useEffect(() => {
      getExtraPositionInformations(query)
        .then((response) => {
          setExtraPositionsInfo(response.data);
          setPagination((prev) => ({
            ...prev,
            page: response.pagination.page,
            size: response.pagination.size,
            total: response.pagination.total,
          }));
        })
        .catch((error) => {
          console.error("Error fetching extra positions info:", error);
        });
    }, [query]);

    const downloadReport = async (fileName: string) => {
      setIsLoading(true);
      if (!query) {
        alert("Please select a filter before downloading the report.");
        setIsLoading(false);
        return;
      }
      try {
        await downloadExtraPositionReport(token, fileName, query);
      } catch (error) {
        alert("Error downloading file");
      } finally {
        setIsLoading(false);
      }
    };

    return {
      handleQuery,
      pagination,
      extraPositionsInfo,
      downloadReport,
      isLoading,
    };
  };
